import React, { Component } from "react";
import {
  Button,
  Col,
  ControlLabel,
  FormControl,
  FormGroup,
  Row,
} from "react-bootstrap";
import { ReactTabulator } from "react-tabulator";
import Popup from "reactjs-popup";
import {
  deleteRecordManagementEntry,
  getRecordManagementEntries,
  saveRecordManagementEntry,
  updateRecordManagementEntry,
} from "../../libs/API";

class DocumentManagementSettingsPopup extends Component {
  constructor(props) {
    super(props);
    this.nextRowId = 1;
    this.tableRef = React.createRef();
    this.state = {
      description: "",
      recordSeries: "",
      retentionPeriod: "",
      validationMessage: "",
      loading: false,
      entries: this.cloneEntries(props.entries),
      isEditing: false,
      editingRowId: null,
      originalDescription: "",
      originalRecordSeries: "",
      originalRetentionPeriod: "",
    };
  }

  componentDidMount() {
    this.loadRecordManagementEntries();
  }

  async componentDidUpdate(prevProps) {
    if (prevProps.selectedClientId !== this.props.selectedClientId) {
      await this.loadRecordManagementEntries();
    }
  }

  cloneEntries(entries) {
    if (!entries || entries.length === 0) {
      return [];
    }

    return entries
      .filter((entry) => {
        return (
          entry.description || entry.recordSeries || entry.retentionPeriod
        );
      })
      .map((entry) => ({
        _rowId: this.nextRowId++,
        id: this.getEntryId(entry),
        description: entry.description || "",
        recordSeries: entry.recordSeries || "",
        retentionPeriod: entry.retentionPeriod || "",
      }));
  }

  getEntryId(entry) {
    if (!entry) {
      return undefined;
    }

    if (entry.id !== undefined && entry.id !== null) {
      return entry.id;
    }
    if (entry.Id !== undefined && entry.Id !== null) {
      return entry.Id;
    }
    if (entry.recordManagementId !== undefined && entry.recordManagementId !== null) {
      return entry.recordManagementId;
    }
    return entry.RecordManagementId;
  }

  hasEntryId(entry) {
    return this.getEntryId(entry) !== undefined && this.getEntryId(entry) !== null;
  }

  getSavedRecord(response) {
    const record =
      response?.data?.record ||
      response?.record ||
      (response?.data &&
      (this.hasEntryId(response.data) ||
        response.data.description ||
        response.data.recordSeries ||
        response.data.retentionPeriod)
        ? response.data
        : null) ||
      (response &&
      (this.hasEntryId(response) ||
        response.description ||
        response.recordSeries ||
        response.retentionPeriod)
        ? response
        : null);

    return record || null;
  }

  getSavedRecordId(response) {
    const candidates = [
      response?.id,
      response?.data?.id,
      response?.record?.id,
      response?.data?.record?.id,
      response?.Id,
      response?.data?.Id,
      this.getEntryId(this.getSavedRecord(response)),
    ];

    return candidates.find((id) => id !== undefined && id !== null);
  }

  normalizeLoadedEntries(data) {
    const records = Array.isArray(data)
      ? data
      : Array.isArray(data && data.data)
        ? data.data
        : Array.isArray(data && data.records)
          ? data.records
          : [];

    return this.cloneEntries(records);
  }

  loadRecordManagementEntries = async () => {
    const { selectedClientId } = this.props;

    if (!selectedClientId) {
      this.setState({ entries: this.cloneEntries(this.props.entries) });
      return;
    }

    this.setState({ loading: true });

    try {
      // TODO: Replace placeholder endpoint with actual backend integration.
      const response = await getRecordManagementEntries(selectedClientId);
      const loadedEntries = this.normalizeLoadedEntries(response);
      this.setState({ entries: loadedEntries, loading: false }, this.refreshTable);
    } catch (error) {
      console.warn("Record Management load placeholder failed:", error);
      this.setState(
        {
          entries: this.cloneEntries(this.props.entries),
          loading: false,
        },
        this.refreshTable,
      );
    }
  };

  getCleanEntries(entries) {
    return entries.map((entry) => ({
      id: this.getEntryId(entry),
      description: entry.description || "",
      recordSeries: entry.recordSeries || "",
      retentionPeriod: entry.retentionPeriod || "",
    }));
  }

  handleInputChange = (field, value) => {
    this.setState({ [field]: value, validationMessage: "" });
  };

  resetEntryForm = () => {
    this.setState({
      description: "",
      recordSeries: "",
      retentionPeriod: "",
      validationMessage: "",
      isEditing: false,
      editingRowId: null,
      originalDescription: "",
      originalRecordSeries: "",
      originalRetentionPeriod: "",
    });
  };

  refreshTable = () => {
    if (this.tableRef.current && this.tableRef.current.table) {
      this.tableRef.current.table.setData(this.state.entries);
    }
  };

  addEntry = async () => {
    if (this.state.isEditing) {
      await this.updateEntry();
      return;
    }

    const description = this.state.description.trim();
    const recordSeries = this.state.recordSeries.trim();
    const retentionPeriod = this.state.retentionPeriod.trim();

    if (!description && !recordSeries && !retentionPeriod) {
      this.setState({
        validationMessage:
          "Please enter Description, Record Series, or Retention Period.",
      });
      return;
    }

    const entry = {
      _rowId: this.nextRowId++,
      description,
      recordSeries,
      retentionPeriod,
    };

    try {
      // TODO: Replace placeholder endpoint with actual backend integration.
      const response = await saveRecordManagementEntry({
        clientId: this.props.selectedClientId,
        description,
        recordSeries,
        retentionPeriod,
      });

      const savedRecord = this.getSavedRecord(response);
      const savedId = this.getSavedRecordId(response);
      if (savedRecord) {
        entry.id = this.getEntryId(savedRecord);
        if (entry.id === undefined || entry.id === null) {
          entry.id = savedId;
        }
        entry.description = savedRecord.description || description;
        entry.recordSeries = savedRecord.recordSeries || recordSeries;
        entry.retentionPeriod = savedRecord.retentionPeriod || retentionPeriod;
      } else if (savedId !== undefined && savedId !== null) {
        entry.id = savedId;
      }
    } catch (error) {
      console.warn("Record Management add placeholder failed:", error);
    }

    this.setState(
      {
        entries: [...this.state.entries, entry],
        description: "",
        recordSeries: "",
        retentionPeriod: "",
        validationMessage: "",
      },
      this.refreshTable,
      );
  };

  editEntry = (rowId) => {
    const entry = this.state.entries.find((item) => item._rowId === rowId);
    if (!entry) {
      return;
    }

    this.setState({
      description: entry.description || "",
      recordSeries: entry.recordSeries || "",
      retentionPeriod: entry.retentionPeriod || "",
      validationMessage: "",
      isEditing: true,
      editingRowId: rowId,
      originalDescription: entry.description || "",
      originalRecordSeries: entry.recordSeries || "",
      originalRetentionPeriod: entry.retentionPeriod || "",
    });
  };

  updateEntry = async () => {
    const description = this.state.description.trim();
    const recordSeries = this.state.recordSeries.trim();
    const retentionPeriod = this.state.retentionPeriod.trim();

    if (!description && !recordSeries && !retentionPeriod) {
      this.setState({
        validationMessage:
          "Please enter Description, Record Series, or Retention Period.",
      });
      return;
    }

    const entry = this.state.entries.find(
      (item) => item._rowId === this.state.editingRowId,
    );
    if (!entry) {
      this.resetEntryForm();
      return;
    }

    if (entry.id === undefined || entry.id === null) {
      console.warn("Record Management update requires id.");
      this.setState({
        validationMessage: "Record Management update requires an id.",
      });
      return;
    }

    const payload = {
      id: entry.id,
      clientId: this.props.selectedClientId,
      description,
      recordSeries,
      retentionPeriod,
    };

    try {
      // TODO: Replace placeholder endpoint with actual backend integration.
      await updateRecordManagementEntry(payload);
    } catch (error) {
      console.warn("Record Management update placeholder failed:", error);
    }

    this.setState(
      {
        entries: this.state.entries.map((item) =>
          item._rowId === this.state.editingRowId
            ? {
                ...item,
                description,
                recordSeries,
                retentionPeriod,
              }
            : item,
        ),
        description: "",
        recordSeries: "",
        retentionPeriod: "",
        validationMessage: "",
        isEditing: false,
        editingRowId: null,
        originalDescription: "",
        originalRecordSeries: "",
        originalRetentionPeriod: "",
      },
      this.refreshTable,
    );
  };

  removeEntry = async (rowId) => {
    const entry = this.state.entries.find((item) => item._rowId === rowId);
    if (!entry) {
      return;
    }

    if (entry.id === undefined || entry.id === null) {
      console.warn("Record Management delete requires id.");
      this.setState({
        validationMessage: "Record Management delete requires an id.",
      });
      return;
    }

    try {
      // TODO: Replace placeholder endpoint with actual backend integration.
      const payload = {
        id: entry.id,
        clientId: this.props.selectedClientId,
      };

      await deleteRecordManagementEntry(payload);
    } catch (error) {
      console.warn("Record Management delete placeholder failed:", error);
    }

    this.setState(
      {
        entries: this.state.entries.filter((item) => item._rowId !== rowId),
      },
      this.refreshTable,
    );
  };

  cancelEdit = () => {
    this.resetEntryForm();
  };

  save = () => {
    this.props.onSave(
      this.getCleanEntries(this.state.entries),
      this.props.selectedClientId,
    );
  };

  cancel = () => {
    this.props.onCancel();
  };

  render() {
    const columns = [
      { title: "Description", field: "description" },
      { title: "Record Series", field: "recordSeries" },
      { title: "Retention Period", field: "retentionPeriod" },
      {
        title: "Action",
        headerSort: false,
        hozAlign: "center",
        formatter: () =>
          "<button type='button' data-action='edit'>Edit</button> <button type='button' data-action='remove'>Remove</button>",
        cellClick: (e, cell) => {
          const action = e.target && e.target.getAttribute("data-action");
          const rowId = cell.getRow().getData()._rowId;

          if (action === "edit") {
            this.editEntry(rowId);
            return;
          }

          if (action === "remove") {
            this.removeEntry(rowId);
          }
        },
      },
    ];

    return (
      <Popup
        open={this.props.show}
        modal
        closeOnDocumentClick={false}
        onClose={this.cancel}
        contentStyle={{
          width: "80%",
          maxWidth: "900px",
          borderRadius: "10px",
          padding: "20px",
          background: "#fff",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.2)",
        }}
      >
        <div>
          {/* eslint-disable-next-line */}
          <a className="close" onClick={this.cancel} style={{ color: "#999" }}>
            &times;
          </a>
          <h3 className="mb-3">Record Management Settings</h3>
          <hr />

          <div className="record-management-popup-body">
            <Row>
              <Col md={3}>
                <FormGroup controlId="recordManagementDescription">
                  <ControlLabel>Description</ControlLabel>
                  <FormControl
                    type="text"
                    value={this.state.description}
                    onChange={(e) =>
                      this.handleInputChange("description", e.target.value)
                    }
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup controlId="recordManagementRecordSeries">
                  <ControlLabel>Record Series</ControlLabel>
                  <FormControl
                    type="text"
                    value={this.state.recordSeries}
                    onChange={(e) =>
                      this.handleInputChange("recordSeries", e.target.value)
                    }
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup controlId="recordManagementRetentionPeriod">
                  <ControlLabel>Retention Period</ControlLabel>
                  <FormControl
                    type="text"
                    value={this.state.retentionPeriod}
                    onChange={(e) =>
                      this.handleInputChange("retentionPeriod", e.target.value)
                    }
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup className="record-management-add">
                  <ControlLabel>&nbsp;</ControlLabel>
                  <Button
                    bsSize="small"
                    bsClass="btn btn-outline-secondary"
                    onClick={this.addEntry}
                  >
                    {this.state.isEditing ? "Update" : "Add"}
                  </Button>{" "}
                  {this.state.isEditing && (
                    <Button
                      bsSize="small"
                      bsClass="btn btn-outline-secondary"
                      onClick={this.cancelEdit}
                    >
                      Cancel Edit
                    </Button>
                  )}
                </FormGroup>
              </Col>
            </Row>

            {this.state.validationMessage && (
              <div className="text-danger">
                {this.state.validationMessage}
              </div>
            )}

            {this.state.loading && <p>Loading Record Management entries...</p>}

            <div className="record-management-grid">
              <ReactTabulator
                ref={this.tableRef}
                data={this.state.entries}
                columns={columns}
                options={{
                  layout: "fitColumns",
                  placeholder: "No Record Management entries added.",
                }}
              />
            </div>
          </div>

          <FormGroup className="document-management-actions">
            <Button bsStyle="info" onClick={this.save}>
              Save
            </Button>{" "}
            <Button bsClass="btn btn-outline-secondary" onClick={this.cancel}>
              Cancel
            </Button>
          </FormGroup>
        </div>
      </Popup>
    );
  }
}

export default DocumentManagementSettingsPopup;
